import { supabase } from '@/lib/supabase';

interface ImageServiceConfig {
  bucketName: string;
}

/**
 * 이미지 서비스 아키텍처 및 최적화 가이드
 * 
 * ## 현재 개발 단계
 * - 미리 준비된 샘플 이미지들을 Storage에 업로드
 * - 원본 이미지를 그대로 받아서 클라이언트 CSS로 크롭/위치 조정
 * 
 * ## 실제 서비스 단계 (향후 구현)
 * 1. 업로드 시: 클라이언트 또는 Edge Function에서 리사이징 후 업로드
 * 2. 출력 시: 리사이징된 이미지를 그대로 출력, CSS object-position으로 위치 조정
 * 
 * ## 이미지 타입별 최적 사양
 * 
 * ### 1. 광고 배너 (Banner)
 * - 컨테이너 크기: 414px × 96px (모바일 기준)
 * - 최적 업로드 사양: 828px × 192px (2x resolution)
 * - 비율: 4:1 (가로:세로)
 * - 포맷: JPEG/WebP
 * - 품질: 90%
 * - 용도: 홈페이지 상단 프로모션 배너
 * 
 * ### 2. 매장 썸네일 (Restaurant Thumbnail)
 * - 컨테이너 크기: 360px × 100px (세로형 카드)
 * - 최적 업로드 사양: 720px × 200px (2x resolution)
 * - 비율: 18:5 (3.6:1, 가로:세로)
 * - 포맷: JPEG/WebP
 * - 품질: 85%
 * - 위치: object-position: top center (상단 중앙에서 크롭)
 * - 용도: 매장 카드 상단 이미지
 * 
 * ### 3. 매장 상세 이미지 (Restaurant Detail)
 * - 컨테이너 크기: 414px × 200px (상세페이지)
 * - 최적 업로드 사양: 828px × 400px (2x resolution)
 * - 비율: 2:1 (가로:세로)
 * - 포맷: JPEG/WebP
 * - 품질: 90%
 * - 용도: 매장 상세페이지 메인 이미지
 * 
 * ### 4. 상품 이미지 (Product Image)
 * - 컨테이너 크기: 120px × 120px (정사각형)
 * - 최적 업로드 사양: 240px × 240px (2x resolution)
 * - 비율: 1:1 (정사각형)
 * - 포맷: JPEG/WebP
 * - 품질: 85%
 * - 위치: object-position: center center
 * - 용도: 상품 리스트, 주문 내역
 * 
 * ## 사용자 업로드 가이드라인
 * - 파일 크기: 최대 5MB
 * - 지원 포맷: JPEG, PNG, WebP
 * - 권장 해상도: 위 사양의 2배수 (Retina 대응)
 * - 자동 리사이징: 업로드 시 최적 크기로 변환
 * - 자동 최적화: WebP 변환, 품질 최적화
 * 
 * ## Storage 구조
 * bucket1/
 * ├── banners/           # 광고 배너
 * │   ├── original/      # 원본 (828×192)
 * │   └── optimized/     # 최적화된 버전
 * ├── restaurants/       # 매장 이미지
 * │   ├── thumbnails/    # 썸네일 (720×200)
 * │   └── details/       # 상세 이미지 (828×400)
 * └── products/          # 상품 이미지
 *     └── thumbnails/    # 상품 썸네일 (240×240)
 */

class ImageService {
  private config: ImageServiceConfig;

  constructor() {
    this.config = {
      bucketName: 'bucket1' // Supabase Storage bucket name
    };
  }

  /**
   * Edge Function 호출 유틸 (multipart)
   * - 단일 마스터 WebP 생성 후 Storage 저장 + DB 자동 갱신
   */
  private async uploadViaEdge(params: {
    file: File;
    category: 'restaurant' | 'product' | 'review';
    entityId: string;
    sequence?: number;
  }): Promise<{ success: boolean; url?: string; error?: string }> {
    try {
      const { file, category, entityId, sequence } = params;
      const { data: sessionData } = await supabase.auth.getSession();
      const accessToken = sessionData.session?.access_token;
      if (!accessToken) {
        return { success: false, error: '인증이 필요합니다.' };
      }

      const functionsUrl = `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/image-resize-upload`;
      const form = new FormData();
      form.append('file', file);
      form.append('category', category);
      form.append('entity_id', entityId);
      if (typeof sequence === 'number') form.append('sequence', String(sequence));

      const res = await fetch(functionsUrl, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${accessToken}`
        },
        body: form
      });
      const json = await res.json();
      if (!res.ok || !json?.success) {
        return { success: false, error: json?.error || '이미지 처리 실패' };
      }
      return { success: true, url: json.url };
    } catch (error) {
      console.error('Edge Function 호출 실패:', error);
      return { success: false, error: '이미지 처리 중 오류가 발생했습니다.' };
    }
  }

  /**
   * Supabase Storage에서 Public 이미지 URL 가져오기
   */
  getPublicImageUrl(path: string, options?: {
    width?: number;
    height?: number;
    quality?: number;
    resize?: 'cover' | 'contain' | 'fill';
  }): string {
    // 경로가 /로 시작하면 제거
    const cleanPath = path.startsWith('/') ? path.slice(1) : path;
    
    // Supabase Storage API 사용
    const { data } = supabase.storage
      .from(this.config.bucketName)
      .getPublicUrl(cleanPath, {
        transform: options ? {
          width: options.width,
          height: options.height,
          quality: options.quality || 80,
          resize: options.resize || 'cover'
        } : undefined
      });
    return data.publicUrl;
  }

  /**
   * 광고 배너 이미지 URL 가져오기
   * 모바일에 최적화된 크기로 변환
   */
  getAdBannerUrl(filename: string = 'adbanner1.jpg'): string {
    // 414px 모바일 컨테이너 기준 (414 - 32px 좌우 마진 = 382px)
    // 4:1 비율로 높이 96px 설정
    return this.getPublicImageUrl(`images/${filename}`, {
      width: 382,
      height: 96,
      quality: 90,
      resize: 'fill'
    });
  }

  /**
   * 모든 광고 배너 이미지 URL 배열 가져오기
   */
  getAllAdBannerUrls(): string[] {
    const bannerFiles = ['adbanner1.jpg', 'adbanner2.jpg'];
    return bannerFiles.map(filename => this.getAdBannerUrl(filename));
  }

  /**
   * 배너 이미지 URL 가져오기 (banners 테이블 연동용)
   * 모바일에 최적화된 크기로 변환
   */
  getBannerUrl(filename: string): string {
    // banners 폴더의 이미지를 414px 모바일 컨테이너 기준으로 최적화
    // 4:1 비율로 높이 96px 설정
    return this.getPublicImageUrl(`banners/${filename}`, {
      width: 382,
      height: 96,
      quality: 90,
      resize: 'fill'
    });
  }

  /**
   * 매장 이미지 URL 가져오기 (레거시 - 매장명 기반)
   * 매장 카드에 최적화된 크기로 변환
   */
  getRestaurantImageByName(restaurantName: string): string {
    // 매장명에 따른 파일명 매핑
    const nameToFileMap: { [key: string]: string } = {
      '캥거루스 서울': 'kangaroo_1.jpg',
      '코알라스 서울': 'koala_1.jpg', 
      '쿼카스 서울': 'quokka_1.jpg'
    };
    
    // 매장명에 해당하는 파일명 찾기, 없으면 기본값
    const filename = nameToFileMap[restaurantName] || 'kangaroo_1.jpg';
    
    // 원본 이미지를 그대로 가져와서 클라이언트에서 object-position으로 제어
    return this.getPublicImageUrl(`restaurants/${filename}`);
  }

  /**
   * 이미지 프리로드 (성능 최적화)
   */
  preloadImage(url: string): Promise<void> {
    return new Promise((resolve, reject) => {
      const img = new Image();
      img.onload = () => resolve();
      img.onerror = () => reject(new Error(`Failed to load image: ${url}`));
      img.src = url;
    });
  }

  /**
   * 여러 이미지 프리로드
   */
  async preloadImages(urls: string[]): Promise<void> {
    await Promise.all(urls.map(url => this.preloadImage(url)));
  }

  /**
   * 이미지 업로드 (향후 확장용)
   */
  async uploadImage(file: File, path: string): Promise<string | null> {
    try {
      const { data, error } = await supabase.storage
        .from(this.config.bucketName)
        .upload(path, file, {
          cacheControl: '3600',
          upsert: false
        });

      if (error) {
        console.error('Image upload error:', error);
        return null;
      }

      return this.getPublicImageUrl(data.path);
    } catch (error) {
      console.error('Image upload failed:', error);
      return null;
    }
  }

  /**
   * 이미지 삭제 (향후 확장용)
   */
  async deleteImage(path: string): Promise<boolean> {
    try {
      if (!path || typeof path !== 'string') {
        console.warn('유효하지 않은 파일 경로, 삭제 건너뜀:', path);
        return true;
      }


      const { error } = await supabase.storage
        .from(this.config.bucketName)
        .remove([path]);

      if (error) {
        // 파일이 존재하지 않는 경우는 이미 삭제된 것으로 간주
        if (error.message?.includes('not found') || error.message?.includes('does not exist')) {
          return true;
        }

        console.error('Storage 이미지 삭제 에러:', {
          error: error.message,
          bucket: this.config.bucketName,
          path
        });
        return false;
      }

      return true;
    } catch (error) {
      console.error('이미지 삭제 중 예외:', { error, path });
      return false;
    }
  }

  /**
   * 매장 이미지 URL 가져오기 (새 restaurant_images 테이블용)
   * 여러 크기 옵션 지원
   */
  getRestaurantImageUrl(filename: string, type: 'thumbnail' | 'detail' | 'original' = 'thumbnail'): string {
    const options = {
      thumbnail: { width: 720, height: 200, quality: 85 }, // 매장 카드용
      detail: { width: 828, height: 400, quality: 90 },    // 상세페이지용
      original: undefined // 원본 크기
    };

    return this.getPublicImageUrl(`restaurants/${filename}`, options[type]);
  }

  /**
   * 제품 이미지 URL 가져오기 (기존 방식 유지)
   */
  getProductImageUrl(filename: string, size: 'thumbnail' | 'detail' = 'thumbnail'): string {
    const options = {
      thumbnail: { width: 240, height: 240, quality: 85 }, // 리스트용
      detail: { width: 480, height: 480, quality: 90 }     // 상세페이지용
    };

    return this.getPublicImageUrl(`products/${filename}`, options[size]);
  }

  /**
   * 리뷰 이미지 URL 가져오기 (새 review_images 테이블용)
   */
  getReviewImageUrl(filename: string, size: 'thumbnail' | 'detail' = 'thumbnail'): string {
    const options = {
      thumbnail: { width: 200, height: 200, quality: 80 }, // 리스트용 정사각형
      detail: { width: 400, height: 400, quality: 85 }     // 확대보기용
    };

    return this.getPublicImageUrl(`review_images/${filename}`, options[size]);
  }

  /**
   * Storage URL에서 파일 경로 추출
   * 삭제 시 필요한 유틸리티 함수
   */
  extractFilePathFromUrl(url: string): string | null {
    try {
      if (!url || typeof url !== 'string') {
        console.error('유효하지 않은 URL:', url);
        return null;
      }

      // Transform URL이 포함된 경우 제거 (쿼리 파라미터 제거)
      const cleanUrl = url.split('?')[0];

      // Supabase Storage URL 형식: https://[project].supabase.co/storage/v1/object/public/bucket1/path/file.jpg
      const match = cleanUrl.match(/\/storage\/v1\/object\/public\/[^\/]+\/(.+)/);
      if (match && match[1]) {
        const filePath = decodeURIComponent(match[1]);
        return filePath;
      }
      
      // 레거시 방식 fallback
      const urlParts = cleanUrl.split('/');
      const bucketIndex = urlParts.findIndex(part => part === this.config.bucketName);
      
      if (bucketIndex === -1 || bucketIndex === urlParts.length - 1) {
        console.error('버킷을 URL에서 찾을 수 없음:', cleanUrl);
        return null;
      }

      const filePath = urlParts.slice(bucketIndex + 1).join('/');
      const decodedPath = decodeURIComponent(filePath);
      return decodedPath;
    } catch (error) {
      console.error('URL에서 파일 경로 추출 실패:', { error, url });
      return null;
    }
  }

  /**
   * 다중 이미지 업로드 (매장/리뷰용)
   * 순서 관리 및 최대 개수 제한 포함
   */
  async uploadMultipleImages(
    files: File[], 
    folder: string, 
    maxCount: number = 3
  ): Promise<{ success: boolean; urls?: string[]; error?: string }> {
    try {
      if (files.length > maxCount) {
        return { success: false, error: `최대 ${maxCount}장까지 업로드 가능합니다.` };
      }

      const uploadPromises = files.map(async (file, index) => {
        const fileName = `${Date.now()}_${index}_${file.name}`;
        const filePath = `${folder}/${fileName}`;
        
        const { data, error } = await supabase.storage
          .from(this.config.bucketName)
          .upload(filePath, file, {
            cacheControl: '3600',
            upsert: false
          });

        if (error) throw error;
        return this.getPublicImageUrl(data.path);
      });

      const urls = await Promise.all(uploadPromises);
      return { success: true, urls };
    } catch (error) {
      console.error('Multiple image upload failed:', error);
      return { success: false, error: '이미지 업로드에 실패했습니다.' };
    }
  }

  /**
   * Storage-DB 동기화를 위한 이미지 삭제
   * URL에서 파일 경로를 추출하여 Storage에서 삭제
   */
  async deleteImageByUrl(imageUrl: string): Promise<boolean> {
    try {
      if (!imageUrl || typeof imageUrl !== 'string') {
        console.warn('유효하지 않은 이미지 URL, 삭제 건너뜀:', imageUrl);
        return true; // 유효하지 않은 URL은 삭제할 것이 없으므로 true 반환
      }

      const filePath = this.extractFilePathFromUrl(imageUrl);
      if (!filePath) {
        console.error('URL에서 파일 경로를 추출할 수 없음:', imageUrl);
        return false;
      }

      const success = await this.deleteImage(filePath);
      
      if (!success) {
        console.error('이미지 삭제 실패:', { imageUrl, filePath });
      }
      
      return success;
    } catch (error) {
      console.error('이미지 삭제 중 예외 발생:', { error, imageUrl });
      return false;
    }
  }

  /**
   * 레스토랑 이미지 업로드 (판매자 전용)
   * 기존 이미지가 있으면 삭제 후 새 이미지 업로드
   */
  async uploadRestaurantImage(
    file: File, 
    restaurantId: string
  ): Promise<{ success: boolean; url?: string; error?: string }> {
    try {
      // Edge Function이 리사이징/저장/DB갱신/이전 파일 삭제까지 처리
      const result = await this.uploadViaEdge({ file, category: 'restaurant', entityId: restaurantId });
      return result;

    } catch (error) {
      console.error('Restaurant image upload failed:', error);
      return { success: false, error: '이미지 업로드 중 오류가 발생했습니다.' };
    }
  }

  /**
   * 레스토랑 이미지 삭제 (판매자 전용)
   */
  async deleteRestaurantImage(imageUrl: string): Promise<boolean> {
    try {
      const filePath = this.extractFilePathFromUrl(imageUrl);
      if (!filePath) {
        console.error('Could not extract file path from URL:', imageUrl);
        return false;
      }

      // restaurant_images 폴더인지 확인
      if (!filePath.startsWith('restaurant_images/')) {
        console.error('Invalid restaurant image path:', filePath);
        return false;
      }

      return await this.deleteImage(filePath);
    } catch (error) {
      console.error('Restaurant image delete failed:', error);
      return false;
    }
  }

  /**
   * 특정 상품의 모든 이미지 파일 목록 조회
   * Storage에서 product_${productId}로 시작하는 모든 파일 검색
   */
  async listProductImages(userId: string, productId: string): Promise<string[]> {
    try {
      const folderPath = `product_images/${userId}/`;
      
      const { data, error } = await supabase.storage
        .from(this.config.bucketName)
        .list(folderPath, {
          limit: 100,
          offset: 0
        });

      if (error) {
        console.error('상품 이미지 목록 조회 실패:', error);
        return [];
      }

      if (!data) {
        return [];
      }

      // product_${productId}로 시작하는 파일들만 필터링
      const productFiles = data
        .filter(file => file.name.startsWith(`product_${productId}`))
        .map(file => `${folderPath}${file.name}`);

      return productFiles;
    } catch (error) {
      console.error('상품 이미지 목록 조회 중 예외:', error);
      return [];
    }
  }

  /**
   * 상품 이미지 업로드 (판매자 전용)
   * 기존 이미지가 있으면 삭제 후 새 이미지 업로드
   * 캐싱 문제 해결을 위해 타임스탬프가 포함된 고유 파일명 사용
   */
  async uploadProductImage(
    file: File,
    productId: string
  ): Promise<{ success: boolean; url?: string; error?: string }> {
    try {
      // Edge Function이 리사이징/저장/DB갱신까지 처리
      const result = await this.uploadViaEdge({ file, category: 'product', entityId: productId });
      return result;

    } catch (error) {
      console.error('Product image upload failed:', error);
      return { success: false, error: '이미지 업로드 중 오류가 발생했습니다.' };
    }
  }
}

// 싱글톤 인스턴스 생성 및 export
export const imageService = new ImageService();

// 타입 export
export type { ImageServiceConfig };
